import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import { createUser, updateUser } from '@/lib/userService';
import { useToast } from '@/components/feature/ToastProvider';
import { useAuth } from '@/contexts/AuthContext';
import { logAudit } from '@/lib/audit';
import type { UserAccount, UserRole, UserStatus } from '@/types';

interface UserFormModalProps {
  open: boolean;
  user: UserAccount | null;
  onClose: () => void;
  onSaved: () => void;
}

const ROLE_OPTIONS = [
  { value: 'Usuario', label: 'Usuario' },
  { value: 'superUsuario', label: 'Super usuario' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVO', label: 'Activo' },
  { value: 'INACTIVO', label: 'Inactivo' },
];

export default function UserFormModal({ open, user, onClose, onSaved }: UserFormModalProps) {
  const { notify } = useToast();
  const { profile } = useAuth();
  const isEdit = Boolean(user);

  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [role, setRole] = useState<UserRole>('Usuario');
  const [status, setStatus] = useState<UserStatus>('ACTIVO');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFullName(user?.full_name ?? '');
    setUsername(user?.username ?? '');
    setPassword('');
    setConfirm('');
    setShow(false);
    setRole(user?.role ?? 'Usuario');
    setStatus(user?.status ?? 'ACTIVO');
    setErrors({});
  }, [open, user]);

  if (!open) return null;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!fullName.trim()) next.fullName = 'El nombre completo es obligatorio.';
    if (!username.trim()) next.username = 'El usuario es obligatorio.';
    else if (!/^[a-z0-9._-]{3,}$/.test(username.trim().toLowerCase())) {
      next.username = 'Use al menos 3 caracteres (letras, números, punto, guion o guion bajo).';
    }
    if (!isEdit) {
      if (password.length < 6) next.password = 'La contraseña debe tener al menos 6 caracteres.';
      if (confirm !== password) next.confirm = 'Las contraseñas no coinciden.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    const result = isEdit && user
      ? await updateUser({
          id: user.id,
          full_name: fullName.trim(),
          username: username.trim().toLowerCase(),
          role,
          status,
        })
      : await createUser({
          full_name: fullName.trim(),
          username: username.trim().toLowerCase(),
          password,
          role,
          status,
        });

    setSaving(false);

    if (result.error) {
      notify({ tone: 'error', title: 'No se pudo guardar', message: result.error });
      return;
    }

    await logAudit({
      userId: profile?.id ?? null,
      userName: profile?.full_name || profile?.username || 'Usuario',
      action: isEdit ? 'EDITAR_USUARIO' : 'CREAR_USUARIO',
      module: 'Usuarios',
      recordId: user?.id ?? username.trim().toLowerCase(),
      description: `${isEdit ? 'Usuario actualizado' : 'Usuario creado'}: ${username.trim().toLowerCase()}.`,
    });

    notify({
      tone: 'success',
      title: isEdit ? 'Usuario actualizado' : 'Usuario creado correctamente',
      message: `${fullName.trim()} fue ${isEdit ? 'actualizado' : 'registrado'} con éxito.`,
    });
    onSaved();
  };

  const isPrimaryAdmin = user?.is_primary_admin;

  return (
    <Modal
      open
      title={isEdit ? 'Editar usuario' : 'Nuevo usuario'}
      description={
        isEdit
          ? 'Actualice la información del usuario. La contraseña se cambia por separado.'
          : 'Complete los datos para crear una nueva cuenta de acceso.'
      }
      icon={isEdit ? 'ri-user-settings-line' : 'ri-user-add-line'}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button icon="ri-save-line" onClick={handleSave} loading={saving}>
            {isEdit ? 'Guardar cambios' : 'Crear usuario'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="* Nombre completo"
          name="full_name"
          icon="ri-user-line"
          placeholder="Ej: Uziel Ticona Ledezma"
          value={fullName}
          onChange={(event) => {
            setFullName(event.target.value);
            setErrors((prev) => ({ ...prev, fullName: '' }));
          }}
          error={errors.fullName || null}
        />

        <Input
          label="* Usuario"
          name="username"
          icon="ri-at-line"
          placeholder="Ej: juan.perez"
          value={username}
          autoComplete="off"
          onChange={(event) => {
            setUsername(event.target.value);
            setErrors((prev) => ({ ...prev, username: '' }));
          }}
          error={errors.username || null}
          hint={isPrimaryAdmin ? 'Administrador principal: conserve el acceso.' : 'Se usa para iniciar sesión.'}
        />

        {!isEdit && (
          <>
            <Input
              label="* Contraseña"
              name="password"
              type={show ? 'text' : 'password'}
              icon="ri-lock-line"
              value={password}
              autoComplete="new-password"
              onChange={(event) => {
                setPassword(event.target.value);
                setErrors((prev) => ({ ...prev, password: '' }));
              }}
              error={errors.password || null}
              trailing={
                <button
                  type="button"
                  onClick={() => setShow((prev) => !prev)}
                  aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
                >
                  <i className={`${show ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
                </button>
              }
            />
            <Input
              label="* Confirmar contraseña"
              name="confirm_password"
              type={show ? 'text' : 'password'}
              icon="ri-lock-line"
              value={confirm}
              autoComplete="new-password"
              onChange={(event) => {
                setConfirm(event.target.value);
                setErrors((prev) => ({ ...prev, confirm: '' }));
              }}
              error={errors.confirm || null}
            />
          </>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="* Rol"
            name="role"
            value={role}
            onChange={(event) => setRole(event.target.value as UserRole)}
            options={ROLE_OPTIONS}
            disabled={isPrimaryAdmin}
          />
          <Select
            label="* Estado"
            name="status"
            value={status}
            onChange={(event) => setStatus(event.target.value as UserStatus)}
            options={STATUS_OPTIONS}
            disabled={isPrimaryAdmin}
          />
        </div>

        {isPrimaryAdmin && (
          <div className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-amber-700">
              <i className="ri-shield-star-line text-base"></i>
            </span>
            <p className="text-xs text-amber-800">
              El administrador principal del sistema conserva siempre el rol superUsuario y el estado
              activo. No puede eliminarse.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}